import {
    BannerSlots,
    Buttons,
    CarouselProducts,
    FeaturedProducts,
    ImageWithText,
    SingleBanner,
    TextHeadline,
} from "@/Slice"
import { MarketAccordions } from "@/Slice/Accordions"
import { EmbedContent } from "@/Slice/EmbedContent"
import { GuidedSelling } from "@/Slice/GuidedSelling"
import { ImageText } from "@/Slice/ImageText"
import { TextComponent } from "@/Slice/TextComponent"
import { VideoBanner } from "@/Slice/VideoBanner"
import { Container, ErrorBoundary } from "@/components"
import { MarketingContentDocument } from "@/lib/prismicio/types.generated"
import type * as prismicT from "@prismicio/types"
import { FC, PropsWithChildren } from "react"

export const SliceRender = ({ slices }: PropsWithChildren<{ slices: MarketingContentDocument["data"]["body"] }>) => (
    <div>
        {[...slices]
            .filter((slice) => !slice.primary.is_hide_module)
            .map((slice, index) => (
                <Slice key={index} slice={slice} lcp={index < 2} />
            ))}
    </div>
)
const BASE_MARGIN = "mt-0.5 first:mt-0"

const SliceContainer = <S extends prismicT.Slice>({
    SliceComponent,
    slice,
    isFullWidth,
    lcp,
}: {
    SliceComponent: FC<{ slice: S; full?: boolean; lcp: boolean }>
    slice: S
    isFullWidth?: boolean
    lcp: boolean
}) => {
    return (
        <Container className={BASE_MARGIN} isFullWidth={isFullWidth}>
            <ErrorBoundary system="CMS">
                <SliceComponent slice={slice} full={isFullWidth} lcp={lcp} />
            </ErrorBoundary>
        </Container>
    )
}

export const Slice = ({
    slice,
    lcp,
}: PropsWithChildren<{ slice: MarketingContentDocument["data"]["body"][number]; lcp: boolean }>) => {
    switch (slice.slice_type) {
        case "video_banner": {
            return (
                <SliceContainer
                    slice={slice}
                    SliceComponent={VideoBanner}
                    isFullWidth={slice.primary.is_full_width}
                    lcp={lcp}
                />
            )
        }
        case "single_banner": {
            return (
                <SliceContainer
                    slice={slice}
                    SliceComponent={SingleBanner}
                    isFullWidth={slice.primary.is_full_width}
                    lcp={lcp}
                />
            )
        }
        case "text_headline": {
            return <SliceContainer slice={slice} SliceComponent={TextHeadline} lcp={lcp} />
        }
        case "text_component": {
            return <SliceContainer slice={slice} SliceComponent={TextComponent} lcp={lcp} />
        }
        case "two_slot_banner":
        case "three_slot_banner":
        case "four_slot_banner":
        case "six_slot_banner": {
            return (
                <SliceContainer
                    slice={slice}
                    SliceComponent={BannerSlots}
                    isFullWidth={slice.primary.is_full_width}
                    lcp={lcp}
                />
            )
        }
        case "embed_content": {
            return <SliceContainer slice={slice} SliceComponent={EmbedContent} lcp={lcp} />
        }
        case "image___text": {
            return <SliceContainer slice={slice} SliceComponent={ImageText} lcp={lcp} />
        }
        case "featured_products": {
            return (
                <SliceContainer
                    slice={slice}
                    SliceComponent={FeaturedProducts}
                    isFullWidth={slice.primary.is_full_width}
                    lcp={lcp}
                />
            )
        }
        case "carousel_products": {
            return (
                <SliceContainer
                    slice={slice}
                    SliceComponent={CarouselProducts}
                    isFullWidth={slice.primary.is_full_width}
                    lcp={lcp}
                />
            )
        }
        case "guided_selling": {
            return (
                <SliceContainer
                    slice={slice}
                    SliceComponent={GuidedSelling}
                    isFullWidth={slice.primary.is_full_width}
                    lcp={lcp}
                />
            )
        }
        case "buttons": {
            return <SliceContainer SliceComponent={Buttons} slice={slice} lcp={lcp} />
        }
        case "image_with_text": {
            return <SliceContainer SliceComponent={ImageWithText} slice={slice} isFullWidth lcp={lcp} />
        }
        case "accordions": {
            return <SliceContainer SliceComponent={MarketAccordions} slice={slice} lcp={lcp} />
        }
        default: {
            return <></>
        }
    }
}
